import { getSelectedModelsCommand } from '@blocksuite/affine/shared/commands';
import type {
  ToolbarAction,
  ToolbarModuleConfig,
} from '@blocksuite/affine/shared/services';

import {
  BranchIcon,
  MindmapToolbarIcon,
  NotesIcon,
} from './curiescious-icons';

interface CitationToken {
  raw: string;
  type: 'pubmed' | 'doi' | 'arxiv';
  identifier: string;
}

interface CuriesciousActionDetail {
  text: string;
  citationTokens: CitationToken[];
  sourceDocId?: string;
  /** First block id touched by the selection — used for click-to-jump backlinks. */
  sourceBlockId?: string;
  /** Viewport rect of the selection at dispatch time — anchor for the type-picker popover. */
  sourceRect?: { x: number; y: number; width: number; height: number };
  /** Inline range inside the source block — used to paint the source highlight precisely on the selected slice. */
  sourceRange?: { index: number; length: number };
}

const PMID_RE = /\[PMID:\s*([0-9]+)\]/gi;
const DOI_RE = /\[DOI:\s*([^\]\s]+)\]/gi;
const ARXIV_RE = /\[arXiv:\s*([^\]\s]+)\]/gi;

function extractCitationTokens(text: string): CitationToken[] {
  const seen = new Set<string>();
  const out: CitationToken[] = [];
  const push = (
    type: CitationToken['type'],
    identifier: string,
    raw: string
  ) => {
    const key = `${type}:${identifier.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ type, identifier, raw });
  };
  for (const m of text.matchAll(PMID_RE)) push('pubmed', m[1], m[0]);
  for (const m of text.matchAll(DOI_RE)) push('doi', m[1], m[0]);
  for (const m of text.matchAll(ARXIV_RE)) push('arxiv', m[1], m[0]);
  return out;
}

interface ToolbarRunCtx {
  chain: any;
  host: any;
}

function collectSelection(ctx: ToolbarRunCtx): CuriesciousActionDetail | null {
  const [ok, { selectedModels }] = ctx.chain
    .pipe(getSelectedModelsCommand, {
      types: ['block', 'text'],
      mode: 'flat',
    })
    .run();
  if (!ok || !selectedModels?.length) return null;

  const text = selectedModels
    .map((model: any) => (model.text ? model.text.toString() : ''))
    .filter(Boolean)
    .join('\n');
  if (!text) return null;

  const sourceDocId: string | undefined =
    ctx.host?.doc?.id ?? ctx.host?.std?.store?.id;
  const sourceBlockId: string | undefined = selectedModels[0]?.id;

  // Capture the inline range (index + length) of the user's selection
  // inside the FIRST block they touched. The source-link formatter
  // uses this to paint the highlight on exactly the selected slice.
  // We only handle the single-block case (the common one); multi-block
  // selections still create a card, but the source highlight is
  // skipped because formatting a multi-block range needs a per-block
  // loop the downstream handler doesn't run yet.
  let sourceRange: { index: number; length: number } | undefined;
  try {
    const selectionValues: any[] = ctx.host?.selection?.value ?? [];
    const textSel = selectionValues.find((s: any) => s.type === 'text');
    if (textSel?.from) {
      const fromBlockId = textSel.from.blockId ?? textSel.blockId;
      if (fromBlockId === sourceBlockId) {
        const idx: number = textSel.from.index ?? 0;
        const len: number = textSel.from.length ?? 0;
        const goesToAnotherBlock =
          !!textSel.to && textSel.to.blockId !== sourceBlockId;
        if (len > 0 && !goesToAnotherBlock) {
          sourceRange = { index: idx, length: len };
        }
      }
    }
  } catch (err) {
    console.debug('[Curiescious toolbar] sourceRange capture failed', err);
  }
  console.debug('[Curiescious toolbar] collected', {
    sourceBlockId,
    hasSourceRange: !!sourceRange,
    sourceRange,
  });

  // Capture the on-screen rect of the user's selection. Downstream UI
  // (e.g. the typed-node picker popover) anchors itself to this rect
  // so the affordance lands next to the highlighted passage instead of
  // jumping to the screen corner. Guarded for non-DOM contexts.
  let sourceRect:
    | { x: number; y: number; width: number; height: number }
    | undefined;
  try {
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    if (sel && sel.rangeCount > 0) {
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (rect && (rect.width > 0 || rect.height > 0)) {
        sourceRect = {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
        };
      }
    }
  } catch {
    /* SSR / no DOM — leave sourceRect undefined */
  }

  return {
    text,
    citationTokens: extractCitationTokens(text),
    sourceDocId,
    sourceBlockId,
    sourceRect,
    sourceRange,
  };
}

function dispatch(
  host: any,
  type: string,
  detail: CuriesciousActionDetail
): void {
  const event = new CustomEvent(type, {
    detail,
    bubbles: true,
  });
  host.dispatchEvent(event);
  console.log(`[Curiescious] ${type}`, detail);
}

function selectionPresent(ctx: ToolbarRunCtx): boolean {
  const [ok, { selectedModels }] = ctx.chain
    .pipe(getSelectedModelsCommand, {
      types: ['block', 'text'],
      mode: 'flat',
    })
    .run();
  return ok && Boolean(selectedModels?.length);
}

/**
 * Add selected text to the Research Canvas (the doc's edgeless surface).
 * Event name kept as `curiescious:add-to-mindmap` for backward compat
 * with the dispatch wiring; the user-facing label is "Add to Canvas".
 */
const addToCanvas: ToolbarAction = {
  id: 'g.add-to-mindmap',
  tooltip: 'Add to Canvas',
  icon: MindmapToolbarIcon,
  when: selectionPresent,
  run(ctx) {
    const detail = collectSelection(ctx);
    if (detail) dispatch(ctx.host, 'curiescious:add-to-mindmap', detail);
  },
};

/**
 * Add selected text to Notes/Manuscript
 */
const addToNotes: ToolbarAction = {
  id: 'h.add-to-notes',
  tooltip: 'Add to Notes',
  icon: NotesIcon,
  when: selectionPresent,
  run(ctx) {
    const detail = collectSelection(ctx);
    if (detail) dispatch(ctx.host, 'curiescious:add-to-notes', detail);
  },
};

/**
 * Create a branch from selected content
 */
const createBranch: ToolbarAction = {
  id: 'i.create-branch',
  tooltip: 'Create Branch',
  icon: BranchIcon,
  when: selectionPresent,
  run(ctx) {
    const detail = collectSelection(ctx);
    if (detail) dispatch(ctx.host, 'curiescious:create-branch', detail);
  },
};

/**
 * Curiescious toolbar configuration
 * Adds custom actions for research workflow:
 * - Add to Canvas (selection becomes a rich card on the doc's edgeless surface)
 * - Add to Notes (Manuscript)
 * - Create Branch
 *
 * Note: "Ask AI" lives in the BlockSuite-native AI toolbar entry
 * (see `ask-ai-toolbar.ts`) and now injects the selection as a
 * quoted reference into the main chat panel input.
 */
export const curiesciousToolbarConfig: ToolbarModuleConfig = {
  actions: [addToCanvas, addToNotes, createBranch],
};
